/** Single credit pack card: pay by card (Polar, USD) or, when enabled, by VietQR bank transfer (SePay, VND) */

import { CreditCard, QrCode, Sparkles } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { buttonPrimaryClass, buttonSecondaryClass, cardClass } from '../ui/styles'
import { cn } from '../../lib/utils'
import { formatPrice } from './credit-format'
import type { CreditPack } from '../../hooks/use-credits'

interface Props {
  pack: CreditPack
  isPopular?: boolean
  /** Any checkout is in flight: all Buy buttons are disabled */
  disabled: boolean
  /** This pack's checkout is in flight */
  isRedirecting: boolean
  onBuy: (packId: string) => void
  /** Offer VietQR bank transfer (SePay is configured and the pack has a VND price) */
  onBankTransfer?: (pack: CreditPack) => void
}

export function CreditPackCard({ pack, isPopular, disabled, isRedirecting, onBuy, onBankTransfer }: Props) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const numberFormat = new Intl.NumberFormat(locale)
  const totalCredits = pack.credits + pack.bonusCredits
  const transfer = onBankTransfer && pack.priceVnd ? onBankTransfer : undefined

  return (
    <div className={cn(cardClass, 'flex flex-col p-5', isPopular && 'border-accent ring-1 ring-accent/30')}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-base font-semibold text-fg">{pack.name}</h3>
        {isPopular && (
          <span className="rounded-md bg-accent-soft px-1.5 py-0.5 text-xs font-medium text-accent-text">
            {t('billing.buy.popular')}
          </span>
        )}
      </div>

      <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-fg">
        {formatPrice(pack.priceCents, 'usd', locale)}
      </p>
      {transfer && pack.priceVnd && (
        <p className="mt-1 text-xs text-fg-subtle tabular-nums">
          {t('billing.buy.priceVnd', { price: formatPrice(pack.priceVnd, 'vnd', locale) })}
        </p>
      )}

      <div className="mt-3 space-y-1">
        <p className="text-sm font-medium text-fg">
          {t('billing.buy.credits', { count: numberFormat.format(totalCredits) })}
        </p>
        {pack.bonusCredits > 0 && (
          <p className="flex items-center gap-1 text-xs text-success">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            {t('billing.buy.bonus', { count: numberFormat.format(pack.bonusCredits) })}
          </p>
        )}
      </div>

      <div className="mt-auto space-y-2 pt-5">
        <button
          type="button"
          onClick={() => onBuy(pack.id)}
          disabled={disabled}
          aria-label={transfer ? t('billing.buy.payCardNamed', { name: pack.name }) : t('billing.buy.buyNamed', { name: pack.name })}
          className={cn(isPopular ? buttonPrimaryClass : buttonSecondaryClass, 'w-full')}
        >
          {transfer && <CreditCard className="h-4 w-4" aria-hidden="true" />}
          {isRedirecting ? t('billing.buy.redirecting') : transfer ? t('billing.buy.payCard') : t('billing.buy.buy')}
        </button>
        {transfer && (
          <button
            type="button"
            onClick={() => transfer(pack)}
            disabled={disabled}
            aria-label={t('billing.buy.payTransferNamed', { name: pack.name })}
            className={cn(buttonSecondaryClass, 'w-full')}
          >
            <QrCode className="h-4 w-4" aria-hidden="true" />
            {t('billing.buy.payTransfer')}
          </button>
        )}
      </div>
    </div>
  )
}
